import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import LanguageIcon from '@mui/icons-material/Language'
import PersonIcon from '@mui/icons-material/Person'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import {
  MetricCard,
  TrendChart,
  DateRangePicker,
  ReferrerBreakdown,
  DeviceBreakdown,
  BenchmarkComparison,
  LocationComparisonChart,
  LocationPerformanceTable,
} from '../../components/Performance'
import {
  useUndertakerAnalytics,
  useChainMemberAnalytics,
  useBenchmarks,
  aggregateSummaries,
  splitSummariesForComparison,
  calculateTrend,
  mergeByDate,
  averageFeedPosition,
  cphDaysAgo,
} from '../../hooks/useAnalytics'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'

interface ClaimedEntity {
  id: string
  name: string
  type: 'undertaker' | 'chain'
  chain_id?: string | null
}

interface ChainLocation {
  id: string
  name: string
  address?: string
}

export default function PerformanceProfile() {
  const { user, isAdmin } = useAuth()
  const [days, setDays] = useState(30)
  const [claimedEntities, setClaimedEntities] = useState<ClaimedEntity[]>([])
  const [selectedEntity, setSelectedEntity] = useState<ClaimedEntity | null>(null)
  const [chainLocations, setChainLocations] = useState<ChainLocation[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string>('all') // 'all' or undertaker_id
  const [loading, setLoading] = useState(true)
  const [locationsLoading, setLocationsLoading] = useState(false)

  // Fetch profiles (all if admin, only claimed if regular user)
  useEffect(() => {
    async function fetchProfiles() {
      if (!user) return
      setLoading(true)
      
      try {
        const entities: ClaimedEntity[] = []
        
        if (isAdmin) {
          // Admin: Fetch ALL chains and standalone undertakers (not part of a chain)
          const { data: undertakers } = await supabase
            .from('undertakers')
            .select('id, name, chain_id')
            .is('chain_id', null)
            .order('name')
          
          if (undertakers) {
            entities.push(...undertakers.map((u) => ({
              id: u.id,
              name: u.name,
              type: 'undertaker' as const,
            })))
          }
          
          const { data: chains } = await supabase
            .from('chains')
            .select('id, name')
            .order('name')
          
          if (chains) {
            entities.push(...chains.map((c) => ({
              id: c.id,
              name: c.name,
              type: 'chain' as const,
            })))
          }
        } else {
          // Regular user: Fetch only claimed standalone undertakers and chains
          const { data: undertakers } = await supabase
            .from('undertakers')
            .select('id, name, chain_id')
            .eq('claimed_by', user.id)
            .eq('claim_approved', true)
            .is('chain_id', null)
            .order('name')
          
          if (undertakers) {
            entities.push(...undertakers.map((u) => ({
              id: u.id,
              name: u.name,
              type: 'undertaker' as const,
            })))
          }
          
          const { data: chains } = await supabase
            .from('chains')
            .select('id, name')
            .eq('claimed_by', user.id)
            .eq('claim_approved', true)
            .order('name')
          
          if (chains) {
            entities.push(...chains.map((c) => ({
              id: c.id,
              name: c.name,
              type: 'chain' as const,
            })))
          }
        }
        
        setClaimedEntities(entities)
        if (entities.length > 0 && !selectedEntity) {
          setSelectedEntity(entities[0])
        }
      } catch (error) {
        console.error('Error fetching profiles:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchProfiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin])

  // Fetch chain locations when chain is selected
  useEffect(() => {
    async function fetchChainLocations() {
      if (selectedEntity?.type !== 'chain') {
        setChainLocations([])
        setSelectedLocation('all')
        return
      }
      
      setLocationsLoading(true)
      try {
        const { data: undertakers } = await supabase
          .from('undertakers')
          .select('id, name, address')
          .eq('chain_id', selectedEntity.id)
          .order('name')
        
        setChainLocations(undertakers || [])
        setSelectedLocation('all')
      } catch (error) {
        console.error('Error fetching chain locations:', error)
        setChainLocations([])
      } finally {
        setLocationsLoading(false)
      }
    }
    
    fetchChainLocations()
  }, [selectedEntity])

  // Fetch analytics based on selected entity and location.
  // Chain "all" view sums the member locations' rows — the chain_id summary rows
  // only contain chain-PAGE events and would under-count massively.
  const isChainAll = selectedEntity?.type === 'chain' && selectedLocation === 'all'
  const undertakerAnalytics = useUndertakerAnalytics(
    selectedEntity?.type === 'undertaker'
      ? selectedEntity.id
      : (selectedEntity?.type === 'chain' && selectedLocation !== 'all')
      ? selectedLocation
      : null,
    days
  )
  const memberAnalytics = useChainMemberAnalytics(
    isChainAll ? chainLocations.map((l) => l.id) : [],
    days
  )

  // Member fetch covers 2x the window; the older half is only for trends
  const currentCutoff = cphDaysAgo(days)
  const memberRows = memberAnalytics.data || []
  const currentMemberRows = memberRows.filter((r) => r.summary_date >= currentCutoff)

  const summaries = isChainAll
    ? mergeByDate(currentMemberRows)
    : undertakerAnalytics.data || []
  const metrics = aggregateSummaries(summaries)
  const { current, previous } = splitSummariesForComparison(summaries, days)
  const feedPosition = averageFeedPosition(isChainAll ? currentMemberRows : summaries)
  const analyticsLoading = isChainAll
    ? locationsLoading || memberAnalytics.isFetching
    : undertakerAnalytics.isFetching

  // Per-location comparison for chart and table — grouped from the same batched
  // fetch, no per-location queries
  const locationComparisons = React.useMemo(() => {
    if (!isChainAll) return []
    return chainLocations.map((loc) => {
      const rows = memberRows.filter((r) => r.undertaker_id === loc.id)
      const currentMetrics = aggregateSummaries(rows.filter((r) => r.summary_date >= currentCutoff))
      const previousMetrics = aggregateSummaries(rows.filter((r) => r.summary_date < currentCutoff))
      return {
        id: loc.id,
        name: loc.name,
        address: loc.address,
        ...currentMetrics,
        trend_views: calculateTrend(currentMetrics.total_profile_views, previousMetrics.total_profile_views),
        trend_phone: calculateTrend(currentMetrics.total_phone_reveals, previousMetrics.total_phone_reveals),
      }
    })
  }, [isChainAll, chainLocations, memberRows, currentCutoff])

  // Fetch benchmarks for comparison
  const { data: benchmark } = useBenchmarks()

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (claimedEntities.length === 0) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {isAdmin 
          ? 'Ingen profiler fundet i systemet.'
          : 'Du har ingen godkendte forretninger endnu. Anmod om at overtage en profil under "Mine forretninger" for at se performance data.'
        }
      </Alert>
    )
  }

  // Separate undertakers and chains for grouped dropdown
  const undertakers = claimedEntities.filter((e) => e.type === 'undertaker')
  const chains = claimedEntities.filter((e) => e.type === 'chain')

  return (
    <Box>
      {/* Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          {/* First dropdown: Select entity (chain or undertaker) */}
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>
              {isAdmin ? 'Vælg profil' : 'Vælg din profil'}
            </InputLabel>
            <Select
              value={selectedEntity?.id || ''}
              label={isAdmin ? 'Vælg profil' : 'Vælg din profil'}
              onChange={(e) => {
                const entity = claimedEntities.find((en) => en.id === e.target.value)
                setSelectedEntity(entity || null)
              }}
            >
              {undertakers.length > 0 && (
                <MenuItem disabled sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.75rem' }}>
                  BEDEMÆND
                </MenuItem>
              )}
              {undertakers.map((entity) => (
                <MenuItem key={entity.id} value={entity.id}>
                  {entity.name}
                </MenuItem>
              ))}
              {chains.length > 0 && (
                <MenuItem disabled sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.75rem', mt: 1 }}>
                  KÆDER
                </MenuItem>
              )}
              {chains.map((entity) => (
                <MenuItem key={entity.id} value={entity.id}>
                  {entity.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Second dropdown: Select location (only for chains) */}
          {selectedEntity?.type === 'chain' && chainLocations.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel>Vælg lokation</InputLabel>
              <Select
                value={selectedLocation}
                label="Vælg lokation"
                onChange={(e) => setSelectedLocation(e.target.value)}
                disabled={locationsLoading}
              >
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontWeight={600}>📊 Alle lokationer (overblik)</Typography>
                  </Box>
                </MenuItem>
                {chainLocations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {loc.name}
                      </Typography>
                      {loc.address && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {loc.address}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <DateRangePicker value={days} onChange={setDays} />
      </Box>

      {/* Current Profile */}
      {selectedEntity && (
        <Card sx={{ mb: 3, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <PersonIcon sx={{ color: 'primary.main' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="h6" fontWeight={600}>
                  {selectedEntity.name}
                </Typography>
                {selectedEntity.type === 'chain' && selectedLocation !== 'all' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={chainLocations.find(l => l.id === selectedLocation)?.name || 'Lokation'}
                      size="small"
                      sx={{
                        backgroundColor: 'primary.light',
                        color: 'white',
                        fontWeight: 500,
                      }}
                    />
                    {chainLocations.find(l => l.id === selectedLocation)?.address && (
                      <Typography variant="body2" color="text.secondary">
                        {chainLocations.find(l => l.id === selectedLocation)?.address}
                      </Typography>
                    )}
                  </Box>
                )}
                {selectedEntity.type === 'chain' && selectedLocation === 'all' && (
                  <Chip
                    label={`Kæde (${chainLocations.length} lokationer)`}
                    size="small"
                    sx={{
                      backgroundColor: 'primary.light',
                      color: 'white',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {analyticsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Metric Cards */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
            <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
              <MetricCard
                title="Profilvisninger"
                value={metrics.total_profile_views}
                trend={calculateTrend(current.total_profile_views, previous.total_profile_views)}
                trendLabel="vs. forrige periode"
                icon={<VisibilityIcon sx={{ fontSize: 20 }} />}
                color="primary"
              />
            </Box>
            <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
              <MetricCard
                title="Unikke besøgende"
                value={metrics.total_profile_views_unique}
                trend={calculateTrend(current.total_profile_views_unique, previous.total_profile_views_unique)}
                trendLabel="vs. forrige periode"
                icon={<PersonIcon sx={{ fontSize: 20 }} />}
                color="info"
              />
            </Box>
            <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
              <MetricCard
                title="Feed visninger"
                value={metrics.total_feed_impressions}
                trend={calculateTrend(current.total_feed_impressions, previous.total_feed_impressions)}
                trendLabel="vs. forrige periode"
                subtitle={`${metrics.total_feed_impressions_unique} unikke${feedPosition != null ? ` · Ø pos. ${feedPosition.toFixed(1)}` : ''}`}
                icon={<FormatListBulletedIcon sx={{ fontSize: 20 }} />}
                color="secondary"
              />
            </Box>
            <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
              <MetricCard
                title="Telefon vist"
                value={metrics.total_phone_reveals}
                trend={calculateTrend(current.total_phone_reveals, previous.total_phone_reveals)}
                trendLabel="vs. forrige periode"
                icon={<PhoneIcon sx={{ fontSize: 20 }} />}
                color="success"
              />
            </Box>
            <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
              <MetricCard
                title="Email vist"
                value={metrics.total_email_reveals}
                trend={calculateTrend(current.total_email_reveals, previous.total_email_reveals)}
                trendLabel="vs. forrige periode"
                icon={<EmailIcon sx={{ fontSize: 20 }} />}
                color="warning"
              />
            </Box>
            <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
              <MetricCard
                title="Hjemmeside klik"
                value={metrics.total_website_clicks}
                trend={calculateTrend(current.total_website_clicks, previous.total_website_clicks)}
                trendLabel="vs. forrige periode"
                icon={<LanguageIcon sx={{ fontSize: 20 }} />}
                color="info"
              />
            </Box>
          </Box>

          {/* Location Performance Table and Chart (only for chains with "all" selected) */}
          {selectedEntity?.type === 'chain' && selectedLocation === 'all' && locationComparisons.length > 0 && (
            <>
              {/* Performance Table */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <LocationPerformanceTable 
                    locations={locationComparisons}
                    onLocationSelect={(locationId) => setSelectedLocation(locationId)}
                  />
                </CardContent>
              </Card>
              
              {/* Comparison Chart */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <LocationComparisonChart 
                    locations={locationComparisons}
                    metrics={[
                      { key: 'total_profile_views', label: 'Visninger', color: '#5c4b99' },
                      { key: 'total_phone_reveals', label: 'Telefon vist', color: '#16a34a' },
                      { key: 'total_email_reveals', label: 'Email vist', color: '#d97706' },
                      { key: 'total_website_clicks', label: 'Hjemmeside klik', color: '#0284c7' },
                    ]}
                  />
                </CardContent>
              </Card>
            </>
          )}

          {/* Charts */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Trend Chart */}
            <Box sx={{ flex: '1 1 600px', minWidth: 300 }}>
              <Card>
                <CardContent>
                  <TrendChart
                    data={summaries}
                    metrics={[
                      { key: 'profile_views', label: 'Visninger', color: '#5c4b99' },
                      { key: 'phone_reveals', label: 'Telefon vist', color: '#16a34a' },
                      { key: 'email_reveals', label: 'Email vist', color: '#d97706' },
                    ]}
                    title="Daglig aktivitet"
                    height={350}
                  />
                </CardContent>
              </Card>
            </Box>

            {/* Device Breakdown */}
            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <DeviceBreakdown metrics={metrics} />
                </CardContent>
              </Card>
            </Box>

            {/* Referrer Breakdown */}
            <Box sx={{ flex: '1 1 600px', minWidth: 300 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <ReferrerBreakdown metrics={metrics} />
                </CardContent>
              </Card>
            </Box>

            {/* Benchmark Comparison */}
            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <BenchmarkComparison 
                    metrics={metrics} 
                    benchmark={benchmark} 
                    days={days}
                  />
                </CardContent>
              </Card>
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}
